import type { EndedHuntDto } from './useEndedHunts'

export interface EndedHuntsSeasonStats {
  /** Number of ended hunts the signed-in user attended. */
  huntsAttended: number
  /** Total time spent in the field across those hunts, in whole hours. */
  hoursInField: number
}

/**
 * CLUB-136: derive the "Your Season" headline metrics from the ended-hunts list
 * — the same `/clubhouse/me/hunts/ended` data the Ended Hunts section uses, so
 * there's a single source of truth (no separate query that could drift).
 *
 * - Hunts attended = count of ended hunts (the endpoint is already scoped to the
 *   signed-in user, i.e. hunts they were a participant on).
 * - Hours in field = summed hunt durations (endTime − startTime) in whole hours.
 *   Open-ended hunts (no endTime) and any non-positive/invalid ranges contribute
 *   nothing rather than skewing the total.
 *
 * Season scoping follows the endpoint's own scope; a narrower "current season"
 * window can be applied server-side once the season boundary is defined.
 */
export function endedHuntsSeasonStats(hunts: EndedHuntDto[]): EndedHuntsSeasonStats {
  let totalMs = 0
  for (const h of hunts) {
    if (!h.endTime) continue
    const start = Date.parse(h.startTime)
    const end = Date.parse(h.endTime)
    if (Number.isNaN(start) || Number.isNaN(end) || end <= start) continue
    totalMs += end - start
  }
  return {
    huntsAttended: hunts.length,
    hoursInField: Math.round(totalMs / 3_600_000),
  }
}
